import { Observable } from 'rxjs';

export type AuthenticationInput = {
	deviceId: string;
	password: string;
};

export type AuthenticationResponse = {
	deviceId: string;
	response: AuthenticationResultCode;
	error?: string;
};
export type AuthenticationResponseSet = {
	deviceResponses: Array<AuthenticationResponse> | undefined;
	response: AuthenticationResultCode;
};

export enum AuthenticationResultCode {
	Ok = 0,
	BadPassword = 1,
	NotAuthenticated = 2,
	Rejected = 3,
	Error = 4
}
export abstract class DeviceAuthenticationCoreApiService {
	/**
	 * Checks authentication state, and optionally verifies with sys Api
	 * @param {string} deviceId
	 * @param {boolean} verify
	 * @returns {boolean}
	 */
	public abstract validateAuthentication(deviceId: string, verify: boolean): Promise<AuthenticationResultCode>;

	/**
	 * Authenticates a set of devices, and caches their credentials.
	 * @param {AuthenticationInput[]} authInfo
	 * @returns {AuthenticationResponseSet}
	 */
	public abstract authenticate(authInfo: AuthenticationInput[]): Observable<AuthenticationResponseSet>;

	/**
	 * Verifies credentials for a set of devices
	 * @param {AuthenticationInput[]} authInfo
	 * @returns {AuthenticationResponseSet}
	 */
	public abstract verify(authInfo: AuthenticationInput[]): Observable<AuthenticationResponseSet>;

	/**
	 * Initialize device to not use Authentication
	 * @param {deviceIds[]} string
	 * @returns {AuthenticationResponseSet}
	 */

	public abstract setupWithoutAuthentication(deviceIds: string[]): Promise<AuthenticationResponseSet>;
	/**
	 * Returns true on successfull password feature removal
	 * @param {string} deviceId
	 * @returns {AuthenticationResultCode}
	 */
	public abstract removePassword(deviceId: string): Promise<AuthenticationResultCode>;

	/**
	 * Set up authentication feature on a set of  devices.
	 * @param {AuthenticationInput[]} authInfo
	 * @returns {AuthenticationResponseSet}
	 */
	public abstract setupPassword(authInfo: AuthenticationInput[]): Observable<AuthenticationResponseSet>;

	/**
	 * Set up authentication feature on device and authenticate.
	 * @param { AuthenticationInput[]} authInfo
	 * @returns {AuthenticationResponseSet}
	 */
	public abstract setupPasswordAndAuthenticate(
		authInfo: AuthenticationInput[]
	): Observable<AuthenticationResponseSet>;

	/**
	 * Returns true on successfull password update
	 * @param {string[]} deviceIds
	 * @param {string} password
	 * @returns {AuthenticationResultCode}
	 */
	public abstract updatePassword(deviceIds: string[], password: string): Observable<AuthenticationResultCode>;

	/**
	 * Select readOnly mode for a device
	 * @param {string} deviceId
	 */
	public abstract selectReadOnlyMode(deviceId: string): void;

	/**
	 * Deselect readOnly mode for a device
	 * @param {string} deviceId
	 */
	public abstract deselectReadOnlyMode(deviceId: string): void;

	/**
	 * Returns true on successfull clear of password from device
	 * @param {string} deviceId
	 * @returns {AuthenticationResultCode}
	 */

	public abstract clearPassword(deviceId: string): Observable<AuthenticationResultCode>;

	/**
	 * reauthenticate devices
	 * @param {string[]} deviceIds
	 */
	public abstract reauthenticate(deviceIds: string[]): Observable<void[]>;
}
